import { Table, Tag, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatDramaTimestamp } from '@/pages/drama-review/utils';
import AuthorNameIdCell from '@/pages/ugc-reports/components/AuthorNameIdCell';
import {
    formatReportTypeLabel,
    getReporterNickname,
    getReporterUserId,
    getReportTypeMeta,
} from '@/pages/ugc-reports/utils';

import type { ReportReasonDictionary } from '@/pages/ugc-reports/utils';
import type { UgcReportLog, UgcReportTicket } from '@/services/ugc-reports';

export const ReportLogsTable: React.FC<{
    logs?: UgcReportLog[];
    dictionary?: ReportReasonDictionary;
}> = ({ logs, dictionary = 'content' }) => {
    const { formatMessage, messages } = useIntl();

    return (
        <Table<UgcReportLog>
            bordered
            size="small"
            rowKey={(row) => row.id}
            dataSource={logs ?? []}
            pagination={false}
            locale={{ emptyText: formatMessage({ id: 'ugcReports.logsEmpty' }) }}
            columns={[
                {
                    title: formatMessage({ id: 'ugcReports.logs.reporter' }),
                    width: 220,
                    render: (_: unknown, log: UgcReportLog) => {
                        const nickname = getReporterNickname(log);
                        const userId = getReporterUserId(log);
                        return <AuthorNameIdCell name={nickname} userId={userId} />;
                    },
                },
                {
                    title: formatMessage({ id: 'ugcReports.logs.reason' }),
                    dataIndex: 'report_type_code',
                    width: 160,
                    render: (code?: string) => {
                        const meta = getReportTypeMeta(code, dictionary);
                        const label = code
                            ? formatReportTypeLabel(
                                  code,
                                  dictionary,
                                  formatMessage,
                                  messages as Record<string, unknown>,
                              )
                            : formatMessage({ id: 'ugcReports.contentType.unknown' });
                        return <Tag color={meta?.color ?? 'default'}>{label}</Tag>;
                    },
                },
                {
                    title: formatMessage({ id: 'ugcReports.logs.description' }),
                    dataIndex: 'description',
                    render: (text?: string | null) => text?.trim() || formatMessage({ id: 'ugcReports.none' }),
                },
                {
                    title: formatMessage({ id: 'ugcReports.logs.submittedAt' }),
                    dataIndex: 'created_at',
                    width: 180,
                    render: (value) => formatDramaTimestamp(value),
                },
            ]}
        />
    );
};

export const renderExpandedLogs = (
    record: UgcReportTicket,
    dictionary: ReportReasonDictionary = 'content',
) => {
    const count = record.report_count ?? record.logs?.length ?? 0;

    return (
        <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                <FormattedMessage id="ugcReports.logs.summary" values={{ count }} />
            </Typography.Text>
            <ReportLogsTable logs={record.logs} dictionary={dictionary} />
        </div>
    );
};
